import sortedIndexBy from 'lodash/sortedIndexBy';

const MIN_GAP_FOR_MOTION = 1000;

export interface SegmentLike {
  start: number;
  end: number;
  intensity: number;
}

/**
 * Finds the index of the first segment in segments where `time` is between
 * the start and end time.  If `time` is in a gap between two segments, this
 * will return the earlier of the two segments.  If `time` is before the first
 * segment, this will return -1.  If `time` is after the last segment,
 * this will return the length of `segments`.
 */
export function findClosestSegmentIndex(
  segments: readonly { start: number; end: number }[],
  time: Date | number,
): number {
  if (segments.length === 0) {
    return -1;
  }

  let index = sortedIndexBy<{ start: number }>(
    segments,
    { start: time.valueOf() },
    (item) => item.start,
  );

  // Because we search on start time, we frequently end off-by-one.
  // If we search for `{start: 4}` in `{start: 3}, {start: 5}`, the result
  // will end up pointing at `{start: 5}` (because `sortedIndexBy` is actually
  // telling us where we have to insert `{start: 4}` to keep sorted order),
  // even though "4" is part of the 3-5 segment, not the 5-7 segment.
  if (segments[index] && segments[index].start > time.valueOf()) {
    index--;
  } else if (
    index >= segments.length &&
    segments[index - 1] &&
    segments[index - 1].end >= time.valueOf()
  ) {
    index--;
  }

  return index;
}

export function isInSegment(segment: SegmentLike | undefined, time: number) {
  return segment && segment.start <= time && segment.end >= time;
}

/**
 * Find the start of the next contiguous block of motion for the specified camera.
 *
 * A block is a contiguous block of segments with motion (where intensity > 1).
 * A gap between two segments counts as non-contiguous.
 *
 * @returns the start time of the given motion block, or undefined if there is no
 *   such block.
 */
export function findNextMotionBlock(
  segments: SegmentLike[],
  masterTime: number,
  direction: 'forwards' | 'backwards',
) {
  const increment = direction === 'forwards' ? 1 : -1;
  let index = findClosestSegmentIndex(segments, masterTime);

  if (index < 0 && direction === 'forwards') {
    return segments.find((segment) => !!segment.intensity)?.start;
  }

  if (index >= segments.length && direction === 'backwards') {
    if (
      segments.length >= 2 &&
      segments[segments.length - 1].intensity > 0 &&
      segments[segments.length - 2].intensity === 0
    ) {
      return segments[segments.length - 1].start;
    }
    index = segments.length - 1;
  }

  if (isInSegment(segments[index], masterTime)) {
    index = index + increment;
  }

  // We want to find the start of the next block of motion, so search until we
  // find a gap or a segment with no motion.  Note that when we're searching
  // estimated motion segments during a motion search, there are no segments
  // with 0 intensity.
  while (
    segments[index] &&
    segments[index - 1] &&
    segments[index].start - segments[index - 1].end < MIN_GAP_FOR_MOTION &&
    segments[index - 1].intensity > 0
  ) {
    index = index + increment;
  }

  if (index >= 0 && index < segments.length) {
    return segments[index].start;
  }

  return undefined;
}
